import { BaseApiRequestHeaders } from '@lumapps/base-api';
import BaseApi from '@lumapps/base-api/api/BaseApi';
import { StreamEvent } from '@lumapps/streaming/types';

import { AskChatParams, AskSearchParams, AskSearchBestAnswer, AskAiChatStreamUpdateDone } from '../types';

export const PATH = 'ask';
export const askAiAPI = new BaseApi({ path: PATH, version: BaseApi.versions.v2 });

/** Build a headers object depending on the given parameters */
const buildHeaders = (headersProps: Pick<AskSearchParams, 'acceptLanguageHeader' | 'traceIdHeader'>) => {
    const { traceIdHeader, acceptLanguageHeader } = headersProps;
    const headers: BaseApiRequestHeaders = {};

    if (traceIdHeader) {
        headers['LumApps-Trace-Id'] = traceIdHeader;
    }

    if (acceptLanguageHeader) {
        headers['Accept-Language'] = acceptLanguageHeader;
    }
    return headers;
};

/**
 * Query the "Ask AI" endpoint for an AI generated answer.
 */
export const getAskAI = async (params: AskSearchParams, signal?: AbortSignal) => {
    const { acceptLanguageHeader, traceIdHeader, ...searchParams } = params;
    const { data } = await askAiAPI.get<AskSearchBestAnswer>('search', {
        params: searchParams,
        signal,
        headers: buildHeaders({ acceptLanguageHeader, traceIdHeader }),
    });
    return data || {};
};

/**
 * Query the "Ask AI" endpoint for a streamed AI generated answer.
 */
export const streamAskAI = async (params: AskChatParams, signal?: AbortSignal) => {
    const { acceptLanguageHeader, traceIdHeader, ...streamParams } = params;
    return askAiAPI.stream<StreamEvent<AskAiChatStreamUpdateDone>>('get', 'chat', {
        params: streamParams,
        signal,
        headers: buildHeaders({ acceptLanguageHeader, traceIdHeader }),
    });
};

export const askAiQueryKeys = {
    all: [PATH] as const,
    search: (params: AskSearchParams) => [...askAiQueryKeys.all, 'search', params] as const,
    chat: (params: AskChatParams) => [...askAiQueryKeys.all, 'chat', params] as const,
};

/**
 * Settings API
 */
/** API path to the ask AI settings */
export const ASK_AI_SETTINGS_PATH = `${PATH}/settings`;
export const askAiSettingsAPI = new BaseApi({ path: ASK_AI_SETTINGS_PATH, version: BaseApi.versions.v2 });

export const askAiSettingsQueryKeys = {
    all: [...askAiQueryKeys.all, 'settings'] as const,
};
